import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { DocumentLang } from '@/app/DocumentLang';
import { SUPPORT_EMAIL } from '@/shared/site';
import { MS_LOCALES, MS_PAGES, msCopyFor, msPath, msPathInLocale, useMsCopy, useMsLocale } from '@/membersheep/i18n';
import memberSheepIcon from '@/membersheep/assets/membersheep-512.png';

const navLinkClass = ({ isActive }: { isActive: boolean }) => (isActive ? 'text-sys-label' : 'hover:text-sys-label');

/** Two-option language switch that keeps the current page. */
const LanguageSwitch = () => {
  const { pathname } = useLocation();
  const locale = useMsLocale();

  return (
    <nav aria-label={msCopyFor(locale).nav.language} className="inline-flex rounded-[9px] bg-sys-separator p-0.5 text-[13px]">
      {MS_LOCALES.map((other) => {
        const copy = msCopyFor(other);
        const current = other === locale;
        return (
          <Link
            key={other}
            to={msPathInLocale(pathname, other)}
            hrefLang={copy.htmlLang}
            lang={copy.htmlLang}
            aria-current={current ? 'true' : undefined}
            className={`rounded-[7px] px-2.5 py-1 ${current ? 'bg-sys-surface text-sys-label shadow-sm' : 'text-sys-secondary'}`}
          >
            {copy.languageLabel}
          </Link>
        );
      })}
    </nav>
  );
};

/** MemberSheep's pages under /membersheep: its own header and footer, light or dark with the system. */
const MemberSheepLayout = () => {
  const copy = useMsCopy();
  const locale = useMsLocale();
  const links = [
    { to: msPath(locale, MS_PAGES.privacy), label: copy.nav.privacy },
    { to: msPath(locale, MS_PAGES.terms), label: copy.nav.terms },
    { to: msPath(locale, MS_PAGES.support), label: copy.nav.support }
  ];

  return (
    <div className="min-h-screen bg-sys-bg font-apple text-sys-label antialiased">
      <DocumentLang lang={copy.htmlLang} />
      <header className="mx-auto flex h-[72px] max-w-[1080px] items-center gap-4 px-4 md:gap-7 md:px-6">
        <div className="flex items-center gap-2.5">
          <Link to={msPath(locale, MS_PAGES.home)} tabIndex={-1} aria-hidden="true" className="shrink-0">
            <img src={memberSheepIcon} alt="" width={30} height={30} className="h-[30px] w-[30px] rounded-[7px]" />
          </Link>
          {/* "by Travo Labs" sits under the name on phones and beside it from md up */}
          <div className="flex flex-col md:flex-row md:items-baseline md:gap-4">
            <Link to={msPath(locale, MS_PAGES.home)} aria-label={copy.nav.home} className="font-display text-[19px] leading-tight font-bold tracking-[-0.01em]">
              MemberSheep
            </Link>
            <Link to="/" className="text-xs md:text-[13px] text-sys-secondary hover:text-sys-label">
              {copy.nav.byLabs}
            </Link>
          </div>
        </div>
        <nav aria-label={copy.nav.primary} className="ml-auto hidden gap-[22px] text-[15px] md:flex text-sys-secondary">
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} className={navLinkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto md:ml-0">
          <LanguageSwitch />
        </div>
      </header>

      <Outlet />

      <footer className="mt-24 border-t border-sys-separator pt-8 pb-12 text-[13px] md:mt-32 text-sys-secondary">
        <div className="mx-auto flex max-w-[1080px] flex-wrap gap-x-6 gap-y-2 px-4 md:px-6">
          <Link to="/" className="mr-auto hover:text-sys-label">
            © {__BUILD_YEAR__} {copy.footer.rights}
          </Link>
          {links.map((link) => (
            <Link key={link.to} to={link.to} className="hover:text-sys-label">
              {link.label}
            </Link>
          ))}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-sys-label">
            {SUPPORT_EMAIL}
          </a>
        </div>
      </footer>
    </div>
  );
};

export default MemberSheepLayout;
